import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useLoaderData, useNavigate, useRevalidator, type LoaderFunctionArgs } from 'react-router-dom';
import { Button, Modal } from '@immo/ui-kit';
import type { AgentSkillDefinitionView } from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { useCan } from '@/lib/auth/permissions';
import { useTranslations } from '@/i18n/use-translations';
import { PageTopbar } from '@/components/layout/topbar-slots';
import { BreadcrumbLeading } from '@/components/layout/page-leading';
import { TagIcon } from '@/components/layout/icons';
import {
  AGENT_SKILLS_PATH,
  AgentSkillForm,
  StatusBadge,
  type AgentSkillFormState,
} from '@/pages/skills/agent-skill-form';

export async function agentSkillEditorLoader({ params }: LoaderFunctionArgs): Promise<AgentSkillDefinitionView> {
  const key = params.key;

  if (key === undefined) throw new Response('Unknown agent skill', { status: 404 });

  const result = await apiClient.adminAgentSkills.get({ pathParams: { key } });

  if (!result.ok) {
    throw new Response('Failed to load the agent skill', { status: result.status });
  }

  return result.body;
}

function errorCodeOf(body: unknown): string | undefined {
  return (body as { error?: { code?: string } } | undefined)?.error?.code;
}

function toFormState(view: AgentSkillDefinitionView): AgentSkillFormState {
  return {
    key: view.key,
    name: view.name,
    description: view.description,
    instructions: view.instructions,
    supportedTriggers: view.supportedTriggers,
    defaultAllowlist: view.defaultAllowlist,
    approvalWindowDays: view.approvalWindowDays,
  };
}

export function AgentSkillEditorPage() {
  const t = useTranslations('pages.admin.skills.agent-skills.editor');
  const tPage = useTranslations('pages.admin.skills.agent-skills');
  const tErrors = useTranslations('errors');
  const intl = useIntl();
  const view = useLoaderData() as AgentSkillDefinitionView;
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const canManage = useCan('backoffice:product-skill:manage');

  const [form, setForm] = useState<AgentSkillFormState>(() => toFormState(view));
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [staleOpen, setStaleOpen] = useState(false);
  const [deactivateOpen, setDeactivateOpen] = useState(false);

  // Re-sync from the server view after a save, a status flip, or a stale reload.
  // Done during render, not in an effect: a mount-time effect could land after
  // the operator's first keystrokes and overwrite them with the loaded value.
  const [syncedView, setSyncedView] = useState(view);

  if (syncedView !== view) {
    setSyncedView(view);
    setForm(toFormState(view));
    setInlineError(null);
  }

  /**
   * A 409/422 is a permanent refusal, never a "try again" (BL-020): the stale
   * edit opens the reload prompt, and every other coded refusal renders its own
   * message from the shared error dictionary, keyed by the code itself.
   */
  const handleResponse = (result: { ok: boolean; status: number; body: unknown }): boolean => {
    if (result.ok) {
      setSaved(true);
      revalidator.revalidate();

      return true;
    }

    const code = errorCodeOf(result.body);

    if (code === 'admin.agent-skill.stale-edit') {
      setStaleOpen(true);

      return false;
    }

    setInlineError(code !== undefined ? tErrors(code) : t('errors.generic'));

    return false;
  };

  const save = async () => {
    setInlineError(null);
    setSaved(false);
    setBusy(true);

    try {
      handleResponse(
        await apiClient.adminAgentSkills.update({
          pathParams: { key: view.key },
          body: {
            name: form.name,
            description: form.description,
            instructions: form.instructions,
            supportedTriggers: [...form.supportedTriggers],
            defaultAllowlist: [...form.defaultAllowlist],
            approvalWindowDays: form.approvalWindowDays,
            expectedVersion: view.version,
          },
        }),
      );
    } finally {
      setBusy(false);
    }
  };

  const activate = async () => {
    setBusy(true);

    try {
      handleResponse(
        await apiClient.adminAgentSkills.activate({
          pathParams: { key: view.key },
          body: { expectedVersion: view.version },
        }),
      );
    } finally {
      setBusy(false);
    }
  };

  const deactivate = async () => {
    setBusy(true);

    try {
      const ok = handleResponse(
        await apiClient.adminAgentSkills.deactivate({
          pathParams: { key: view.key },
          body: { expectedVersion: view.version },
        }),
      );

      if (ok) setDeactivateOpen(false);
    } finally {
      setBusy(false);
    }
  };

  const reloadAfterStale = () => {
    setStaleOpen(false);
    revalidator.revalidate();
  };

  return (
    <>
      <PageTopbar
        leading={
          <BreadcrumbLeading
            icon={TagIcon}
            crumbs={[{ label: tPage('title'), to: AGENT_SKILLS_PATH }, { label: view.name }]}
          />
        }
      />
      <div className="mx-auto max-w-3xl space-y-4 p-6">
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-semibold text-text-primary">{view.name}</h1>
          <StatusBadge status={view.status} label={tPage(`status.${view.status}`)} />
        </div>
        <p className="text-sm text-text-tertiary">
          {view.lastEditedByName !== null
            ? tPage('last-edited-by', {
                name: view.lastEditedByName,
                date: intl.formatDate(view.lastEditedAt, { dateStyle: 'short' }),
              })
            : tPage('never-edited')}
        </p>
        {view.status === 'inactive' && <p className="text-sm text-text-tertiary">{t('inactive-hint')}</p>}

        <AgentSkillForm
          // Remount on every server version so the allowlist field's own text
          // state cannot survive a save or a stale-edit reload and show the
          // operator something the server no longer holds.
          key={view.version}
          value={form}
          onChange={(next) => {
            setForm(next);
            setInlineError(null);
            setSaved(false);
          }}
          keyEditable={false}
          disabled={busy || !canManage}
          error={inlineError}
        />

        {saved && <p className="text-sm text-success-700">{t('saved')}</p>}

        {canManage && (
          <div className="flex items-center gap-2">
            <Button type="button" onClick={save} disabled={busy}>
              {t('save')}
            </Button>
            {view.status === 'inactive' && (
              <Button type="button" variant="secondary" onClick={activate} disabled={busy}>
                {t('activate')}
              </Button>
            )}
            {view.status === 'active' && (
              <Button type="button" variant="ghost" onClick={() => setDeactivateOpen(true)} disabled={busy}>
                {t('deactivate')}
              </Button>
            )}
            <Button type="button" variant="ghost" onClick={() => navigate(AGENT_SKILLS_PATH)} disabled={busy}>
              {t('cancel')}
            </Button>
          </div>
        )}
      </div>

      <Modal
        open={staleOpen}
        onClose={() => setStaleOpen(false)}
        size="sm"
        title={t('stale.title')}
        footer={
          <Button type="button" onClick={reloadAfterStale}>
            {t('stale.reload')}
          </Button>
        }
      >
        <p className="text-sm text-text-secondary">{t('stale.body')}</p>
      </Modal>

      <Modal
        open={deactivateOpen}
        onClose={() => setDeactivateOpen(false)}
        size="sm"
        title={t('deactivate-confirm.title')}
        footer={
          <>
            <Button type="button" variant="ghost" onClick={() => setDeactivateOpen(false)} disabled={busy}>
              {t('deactivate-confirm.cancel')}
            </Button>
            <Button type="button" variant="danger" onClick={deactivate} disabled={busy}>
              {t('deactivate-confirm.confirm')}
            </Button>
          </>
        }
      >
        <p className="text-sm text-text-secondary">{t('deactivate-confirm.body')}</p>
      </Modal>
    </>
  );
}
